import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import * as Mui from "@mui/material";
import {
  Close,
  CloseFullscreen,
} from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { parentBoxStyle } from "services/constants";
import { useSelector } from "react-redux";
import {
  checkFirstLevelApproverPermission,
  checkSecondLevelApproverPermission,
} from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import { useFetch } from "services/hooks/useFetch";
import {
  APP_BAR_OFFSET,
  designColors,
  designRadii,
  designShell,
  designType,
  FORM_ACTION_BUTTON_WIDTH,
  PANEL_DRAWER_WIDTH,
  panelDrawerSx,
} from "services/constants/designTokens";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import {
  GlobalFieldLabel,
  useExpandedContentLeft,
} from "components/DesignSystem/sharedUiPatterns";
import { SelectedEmployeeHeader } from "../SelectedEmployeeHeader";
import { OpenFullViewIcon } from "components/DesignSystem/OpenFullViewIcon";
import { tooltipStyleProps } from "services/constants/Styles";
import { PanelHeaderIconButton } from "components/DesignSystem/GlobalSidePanel";

const dayoffType = [{ label: "Week Off" }, { label: "Holiday" }];

// Create-only — this creates a Planned (Pre-Approval) off-day duty request.
// Retrospective (Post-Approval) records are system-derived from attendance
// and are never manually created here, matching the old CreateDayOff.js.
export const Apply = ({
  onClose,
  setReCallApi,
  open = true,
  expanded = false,
  onToggleExpand,
}) => {
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      start_date: null,
      end_date: null,
      dayoff_type: "",
      reason: "",
      button_loader: false,
    },
  });
  const [showAutoCompleteAvathar, setShowAutoCompleteAvathar] = useState(false);
  const { domain, userDetails, topbarCompanyId, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const { result: users } = useFetch(
    `${domain}list/employee?user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
  );
  // Editor-only submissions must stay pending; approval requires approver access.
  const hasApproverAccess =
    checkFirstLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Off Day Duty",
    ) ||
    checkSecondLevelApproverPermission(
      userScreenAccess?.module_details,
      "Approvals",
      "Off Day Duty",
    );
  const contentLeft = useExpandedContentLeft(expanded);

  useEffect(() => {
    if (open) {
      reset();
      setShowAutoCompleteAvathar(false);
    }
  }, [open, reset]);

  const updateUserName = (event, data) => {
    if (data) {
      setValue("user_name", data?.label);
      setValue("user_id", data?.user_id);
      setValue("selectedUser", data);
      setShowAutoCompleteAvathar(false);
    } else {
      setValue("user_name", null);
      setValue("user_id", "");
      setValue("selectedUser", null);
      setShowAutoCompleteAvathar(true);
    }
  };

  const selectDayoffType = (event, data) => {
    if (data) {
      setValue("dayoff_type", data?.label);
    }
  };

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    const payload = {
      ...(!hasApproverAccess && { status: "Pending" }),
      user_id: data.user_id,
      start_date: data.start_date?.format("YYYY-MM-DD"),
      end_date: data.end_date?.format("YYYY-MM-DD"),
      reason: data.reason,
      approval_type: data.dayoff_type,
    };
    const response = await customAPI(
      `${domain}team_zone/approve/day_off/teamworkspace-dayoff-approve/?company_id=${topbarCompanyId}`,
      "POST",
      payload,
    );
    if (response?.ok) {
      showToast("success", response?.responseDetails?.message);
      setReCallApi(true);
      reset();
      onClose();
      setValue("button_loader", false);
    } else {
      // On failure customAPI already surfaces the error toast.
      setValue("button_loader", false);
    }
  };

  const panelContent = (
    <Box
      sx={{
        ...parentBoxStyle,
        height: "100%",
        borderRadius: expanded ? designRadii.card : 0,
        overflow: "hidden",
        backgroundColor: designColors.white,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          backgroundColor: designColors.white,
        }}
      >
        <Box sx={{ p: 3, pb: 0 }}>
          <Mui.Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
            sx={{
              pb: 2.25,
              borderBottom: `1px solid ${designColors.slate200}`,
            }}
          >
            <Box>
              <Typography sx={designType.panelTitle}>
                New Off-Day Duty Request
              </Typography>
              <Typography sx={{ ...designType.pageSubtitle, mt: 0.5 }}>
                Create a planned off-day duty request for a selected date range.
              </Typography>
            </Box>
            <Mui.Stack direction="row" spacing={1} alignItems="center">
              {onToggleExpand ? (
                expanded ? (
                  <PanelHeaderIconButton
                    expanded
                    title="Collapse"
                    onClick={onToggleExpand}
                  >
                    <CloseFullscreen />
                  </PanelHeaderIconButton>
                ) : (
                  <Mui.Tooltip arrow slotProps={tooltipStyleProps} title="Open full view">
                    <IconButton
                      onClick={onToggleExpand}
                      size="small"
                      sx={{
                        ...designShell.panelSoftIconButton,
                        color: designColors.slate500,
                      }}
                    >
                      <OpenFullViewIcon fontSize="small" />
                    </IconButton>
                  </Mui.Tooltip>
                )
              ) : null}
              {expanded ? (
                <PanelHeaderIconButton
                  expanded
                  title="Close"
                  onClick={onClose}
                >
                  <Close />
                </PanelHeaderIconButton>
              ) : (
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{
                    ...designShell.panelSoftIconButton,
                    color: designColors.slate500,
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Mui.Stack>
          </Mui.Stack>
        </Box>
      </Paper>
      <Mui.Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

            <Box>
              <GlobalFieldLabel helperText="Select the employee for whom the off-day duty request is being raised.">
                Employee Name
              </GlobalFieldLabel>
              <CustomAutoCompleteWithLogo
                dataList={users?.map((obj) => ({
                  ...obj,
                  label: obj.user_name,
                  image: obj?.image_url,
                }))}
                selectedValue={watch("selectedUser")}
                rules={{
                  ...register("user_name", {
                    required: "Employee name is required",
                  }),
                }}
                updateDataFunction={updateUserName}
                placeHolder="Select employee name"
                errorMessage={
                  errors?.user_name?.message ? errors?.user_name?.message : ""
                }
                textBoxError={errors.user_name ? true : false}
                showAvathar={showAutoCompleteAvathar}
                setShowAutoCompleteAvathar={setShowAutoCompleteAvathar}
              />
            </Box>
            <SelectedEmployeeHeader selectedUser={watch("selectedUser")} />
            <Box>
              <GlobalFieldLabel helperText="Select the start date for the off-day period">
                Start Date
              </GlobalFieldLabel>
              <CustomizedDatePicker
                name="start_date"
                control={control}
                required
                requiredMessage="Start date is required"
                placeholder="Select start date"
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Select the end date for the off-day duty period.">
                End Date
              </GlobalFieldLabel>
              <CustomizedDatePicker
                name="end_date"
                control={control}
                required
                requiredMessage="End date is required"
                placeholder="Select end date"
                startDate={watch("start_date")}
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Select the off-day duty type for reference.">
                Off-Day Type
              </GlobalFieldLabel>
              <CustomAutoComplete
                dataList={dayoffType}
                rules={{
                  ...register("dayoff_type", {
                    required: "Off-day duty type is required",
                  }),
                }}
                selectedValue={watch("dayoff_type")}
                updateDataFunction={selectDayoffType}
                placeHolder="Select off-day duty type"
                errorMessage={
                  errors?.dayoff_type?.message
                    ? errors?.dayoff_type?.message
                    : ""
                }
                textBoxError={errors?.dayoff_type ? true : false}
              />
            </Box>
            <Box>
              <GlobalFieldLabel helperText="Enter the purpose or justification for the off-day duty request.">
                Reason
              </GlobalFieldLabel>
              <CustomTextBox
                type="multiline"
                rules={{
                  ...register("reason", {
                    required: "Reason is required",
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else if (value?.trim().length > 255 && value !== "") {
                          return "Maximum length is 255 characters";
                        } else {
                          return true;
                        }
                      },
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "Atleast one alphabet required";
                        }
                      },
                    },
                  }),
                }}
                selectedValue={watch("reason")}
                updateDataFunction={(event) => {
                  if (event.target.value.length <= 1024) {
                    setValue("reason", event.target.value);
                  }
                }}
                placeHolder="Enter reason or justification for off-day duty"
                errorMessage={
                  errors?.reason?.message ? errors?.reason?.message : ""
                }
                textBoxError={errors?.reason ? true : false}
              />
            </Box>
          </Box>
        </Box>
        <Mui.Box
          sx={{
            px: 3,
            py: 1.25,
            borderTop: `1px solid ${designColors.slate200}`,
            backgroundColor: designColors.white,
          }}
        >
          <Mui.Stack direction="row" spacing={1.25} justifyContent="flex-end">
            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton
              type="submit"
              onClick={handleSubmit(onSubmit)}
              loading={Boolean(watch("button_loader"))}
              disabled={Boolean(watch("button_loader"))}
              sx={{ minWidth: FORM_ACTION_BUTTON_WIDTH }}
            >
              {hasApproverAccess ? "Approve" : "Apply"}
            </PrimaryButton>
          </Mui.Stack>
        </Mui.Box>
      </Mui.Box>
    </Box>
  );

  if (expanded) {
    return createPortal(
      <Box
        sx={{
          position: "fixed",
          top: APP_BAR_OFFSET,
          left: `${contentLeft}px`,
          right: 0,
          bottom: 0,
          zIndex: 10,
          overflow: "hidden",
          backgroundColor: designColors.pageBg,
          p: 2,
          boxSizing: "border-box",
        }}
      >
        <Box sx={{ height: "100%", minHeight: "100%", width: "100%" }}>
          {panelContent}
        </Box>
      </Box>,
      document.body,
    );
  }

  return (
    <Mui.Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={panelDrawerSx()}
    >
      <Box sx={{ width: PANEL_DRAWER_WIDTH, maxWidth: "100%", height: "100%" }}>
        {panelContent}
      </Box>
    </Mui.Drawer>
  );
};
